/**
 * F6 — Audit-napló oldal
 * /beallitasok/audit
 *
 * Két tab: Üzleti napló | Technikai napló
 * Szűrhető, lapozható táblázat
 */
import { useState, useEffect, useCallback } from 'react';
import { get } from '../../lib/api';
import { PageHeader, Card, Pagination } from '../../components/ui';

// ── Típusok ──────────────────────────────────────────────────────────────────

interface UzletiRow {
  id: string;
  entitas: string;
  entitas_id: string;
  muvelet: string;
  user_email: string | null;
  idopont: string;
  regi_json: Record<string, unknown> | null;
  uj_json: Record<string, unknown> | null;
}

interface TechRow {
  id: string;
  esemeny: string;
  user_email: string | null;
  ip: string | null;
  reszletek: Record<string, unknown> | null;
  idopont: string;
}

interface AuditResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// ── Segédfüggvények ──────────────────────────────────────────────────────────

function fmt(iso: string) {
  return new Date(iso).toLocaleString('hu-HU', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const ENTITAS: Record<string, string> = {
  SZAMLA: 'Számla', PENZTARBIZONYLAT: 'Pénztárbizonylat', AJANLAT: 'Ajánlat',
  KESZLETMOZGAS: 'Készletmozgás', PARTNER: 'Partner', MEGRENDELES: 'Megrendelés',
  SZALLITOLEVEL: 'Szállítólevél', ELOFIZETES: 'Előfizetés', DOKUMENTUM: 'Dokumentum',
  FELADAT: 'Feladat', PROJEKT: 'Projekt',
};

const MUVELET: Record<string, string> = {
  LETREHOZAS: 'Létrehozás', STORNO: 'Sztornó', ELFOGADAS: 'Elfogadás',
  MODOSITAS: 'Módosítás', TORLESI_KISERLET: 'Törlési kísérlet',
  KIALLITAS: 'Kiállítás', FIZETES: 'Fizetés',
};

const ESEMENY: Record<string, string> = {
  LOGIN: 'Bejelentkezés', JOG_VALTOZAS: 'Jog-változás', IMPERSONATION: 'Megszemélyesítés',
  API_HIBA: 'API hiba', JOGOSULTSAG_MENTES: 'Jogosultság-mentés',
  JELSZOVALTOZAS: 'Jelszóváltozás', MEGHIVAS: 'Meghívás',
  HOZZAFERES_MEGTAGADVA: 'Hozzáférés megtagadva',
};

const MUVELET_COLOR: Record<string, React.CSSProperties> = {
  LETREHOZAS:       { background: '#dcfce7', color: '#166534' },
  KIALLITAS:        { background: '#dbeafe', color: '#1e40af' },
  STORNO:           { background: '#fee2e2', color: '#991b1b' },
  ELFOGADAS:        { background: '#ede9fe', color: '#5b21b6' },
  MODOSITAS:        { background: '#fef9c3', color: '#854d0e' },
  TORLESI_KISERLET: { background: '#ffedd5', color: '#9a3412' },
  FIZETES:          { background: '#ccfbf1', color: '#115e59' },
};

const BADGE_STYLE: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 12,
  fontSize: 11, fontWeight: 600,
};

const TH: React.CSSProperties = {
  padding: '8px 12px', fontWeight: 600, color: 'var(--slate)',
  whiteSpace: 'nowrap', textAlign: 'left', background: '#F4F5F9',
};
const TD: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #E6E8F0' };

// ── Üzleti napló tab ─────────────────────────────────────────────────────────

import type React from 'react';

function UzletiTab() {
  const LIMIT = 25;
  const [page, setPage]       = useState(1);
  const [entitas, setEntitas] = useState('');
  const [muvelet, setMuvelet] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]   = useState('');
  const [result, setResult]   = useState<AuditResult<UzletiRow> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (entitas)  params.set('entitas', entitas);
      if (muvelet)  params.set('muvelet', muvelet);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to', dateTo);
      const r = await get<AuditResult<UzletiRow>>(`/admin/audit/uzleti?${params}`);
      setResult(r);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [page, entitas, muvelet, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setEntitas(''); setMuvelet(''); setDateFrom(''); setDateTo(''); setPage(1); };

  return (
    <div>
      <Card className="mb-4">
        <div className="card-h"><h4>Szűrők</h4></div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '12px 16px 14px' }}>
          <select value={entitas} onChange={e => { setEntitas(e.target.value); setPage(1); }}
            style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}>
            <option value="">Minden entitás</option>
            {Object.entries(ENTITAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={muvelet} onChange={e => { setMuvelet(e.target.value); setPage(1); }}
            style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}>
            <option value="">Minden művelet</option>
            {Object.entries(MUVELET).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }} />
          <button onClick={reset} className="btn btn-ghost btn-sm">Törlés</button>
        </div>
      </Card>

      {loading && <div style={{ textAlign: 'center', color: 'var(--slate)', padding: 32 }}>Betöltés…</div>}
      {error   && <div style={{ textAlign: 'center', color: '#dc2626', padding: 32 }}>Hiba a betöltésnél.</div>}

      {result && !loading && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Időpont', 'Entitás', 'Azonosító', 'Művelet', 'Felhasználó', 'Részletek'].map(h => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.data.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--slate)' }}>Nincs rekord</td></tr>
                )}
                {result.data.map(row => (
                  <tr key={row.id}>
                    <td style={{ ...TD, color: 'var(--slate)', whiteSpace: 'nowrap' }}>{fmt(row.idopont)}</td>
                    <td style={{ ...TD, fontWeight: 600, color: 'var(--navy)' }}>{ENTITAS[row.entitas] ?? row.entitas}</td>
                    <td style={{ ...TD, fontFamily: 'monospace', fontSize: 11, color: 'var(--slate)' }}>{row.entitas_id.slice(0, 8)}…</td>
                    <td style={TD}>
                      <span style={{ ...BADGE_STYLE, ...(MUVELET_COLOR[row.muvelet] ?? { background: '#F3F4F6', color: '#374151' }) }}>
                        {MUVELET[row.muvelet] ?? row.muvelet}
                      </span>
                    </td>
                    <td style={{ ...TD, color: 'var(--slate)' }}>{row.user_email ?? '—'}</td>
                    <td style={{ ...TD, fontSize: 11, color: 'var(--slate)', maxWidth: 220 }}>
                      {row.uj_json
                        ? Object.entries(row.uj_json).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.total > LIMIT && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <Pagination currentPage={page} totalPages={Math.ceil(result.total / LIMIT)} onPageChange={setPage} />
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 8 }}>
            Összesen: {result.total} rekord — append-only, 8 éves megőrzési kötelezettség
          </div>
        </>
      )}
    </div>
  );
}

// ── Technikai napló tab ───────────────────────────────────────────────────────

function TechTab() {
  const LIMIT = 25;
  const [page, setPage]       = useState(1);
  const [esemeny, setEsemeny] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]   = useState('');
  const [result, setResult]   = useState<AuditResult<TechRow> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (esemeny)  params.set('esemeny', esemeny);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to', dateTo);
      const r = await get<AuditResult<TechRow>>(`/admin/audit/tech?${params}`);
      setResult(r);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [page, esemeny, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setEsemeny(''); setDateFrom(''); setDateTo(''); setPage(1); };

  return (
    <div>
      <Card className="mb-4">
        <div className="card-h"><h4>Szűrők</h4></div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '12px 16px 14px' }}>
          <select value={esemeny} onChange={e => { setEsemeny(e.target.value); setPage(1); }}
            style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, minWidth: 200 }}>
            <option value="">Minden esemény</option>
            {Object.entries(ESEMENY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }} />
          <button onClick={reset} className="btn btn-ghost btn-sm">Törlés</button>
        </div>
      </Card>

      {loading && <div style={{ textAlign: 'center', color: 'var(--slate)', padding: 32 }}>Betöltés…</div>}
      {error   && <div style={{ textAlign: 'center', color: '#dc2626', padding: 32 }}>Hiba a betöltésnél.</div>}

      {result && !loading && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Időpont', 'Esemény', 'Felhasználó', 'IP', 'Részletek'].map(h => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.data.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--slate)' }}>Nincs rekord</td></tr>
                )}
                {result.data.map(row => (
                  <tr key={row.id}>
                    <td style={{ ...TD, color: 'var(--slate)', whiteSpace: 'nowrap' }}>{fmt(row.idopont)}</td>
                    <td style={{ ...TD, fontWeight: 600, color: 'var(--navy)' }}>{ESEMENY[row.esemeny] ?? row.esemeny}</td>
                    <td style={{ ...TD, color: 'var(--slate)' }}>{row.user_email ?? '—'}</td>
                    <td style={{ ...TD, fontFamily: 'monospace', fontSize: 11 }}>{row.ip ?? '—'}</td>
                    <td style={{ ...TD, fontSize: 11, color: 'var(--slate)', maxWidth: 260 }}>
                      {row.reszletek
                        ? Object.entries(row.reszletek).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(', ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.total > LIMIT && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <Pagination currentPage={page} totalPages={Math.ceil(result.total / LIMIT)} onPageChange={setPage} />
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 8 }}>
            Összesen: {result.total} rekord
          </div>
        </>
      )}
    </div>
  );
}

// ── Fő oldal ─────────────────────────────────────────────────────────────────

type AuditTab = 'uzleti' | 'tech';

export default function AuditPage() {
  const [tab, setTab] = useState<AuditTab>('uzleti');

  const tabStyle = (t: AuditTab): React.CSSProperties => ({
    padding: '10px 20px', fontWeight: 600, fontSize: 14,
    background: 'none', border: 'none',
    borderBottom: tab === t ? '2px solid var(--orange)' : '2px solid transparent',
    color: tab === t ? 'var(--navy)' : 'var(--slate)',
    cursor: 'pointer', marginBottom: -2, transition: 'color .15s',
  });

  return (
    <div>
      <PageHeader
        title="Audit-napló"
        description="Üzleti és technikai naplók — jogszabályi megfelelés, 8 éves megőrzés"
      />

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #E6E8F0' }}>
        <button style={tabStyle('uzleti')} onClick={() => setTab('uzleti')}>Üzleti napló</button>
        <button style={tabStyle('tech')}   onClick={() => setTab('tech')}>Technikai napló</button>
      </div>

      {tab === 'uzleti' && <UzletiTab />}
      {tab === 'tech'   && <TechTab />}
    </div>
  );
}
